// ABOUTME: Table displaying categories with their config values and level badges.
// ABOUTME: Similar to ProductConfigTable but shows category-level resolution.

import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { colors } from '../../theme/colors';
import { formatHorizon } from '../../utils/formatters';
import LevelBadge from './LevelBadge';

const FIELD_COLUMNS = [
  'color', 'batch_size', 'preparation_minutes', 'baking_minutes',
  'unloading_minutes', 'opening_time', 'closing_time',
  'closing_minutes_before_shop', 'prediction_horizon_minutes',
];

const GRID_TEMPLATE = '1fr 1.5fr 0.5fr 0.6fr 0.6fr 0.6fr 0.7fr 0.6fr 0.6fr 0.7fr 0.7fr';

function formatValue(value, fieldName, units) {
  if (fieldName === 'color') return null; // rendered as swatch
  if (value === null || value === undefined) return '-';
  if (fieldName === 'prediction_horizon_minutes') return formatHorizon(value, units);
  return String(value);
}

export default function CategoryConfigTable({
  categories,
  resolvedCategories,
  searchQuery,
  pendingChanges,
  affectedCells,
  onCellClick,
  onDeleteCategory,
  shopId,
}) {
  const { t } = useTranslation();
  const horizonUnits = { hourUnit: t('common.hours'), minuteUnit: t('common.minutes') };
  const gridTemplate = GRID_TEMPLATE + (onDeleteCategory ? ' 2.5rem' : '');

  const filteredCategories = useMemo(() => {
    let result = resolvedCategories || [];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.id && c.id.toLowerCase().includes(q))
      );
    }
    return result;
  }, [resolvedCategories, searchQuery]);

  return (
    <Box>
      {/* Header row */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: gridTemplate,
        gap: 1,
        px: 1.5,
        py: 1,
        borderBottom: `2px solid ${colors.border.light}`,
      }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.text.secondary }}>
          ID
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.text.secondary }}>
          {t('config.fields.name')}
        </Typography>
        {FIELD_COLUMNS.map(col => (
          <Typography key={col} sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.text.secondary }}>
            {t(`config.fields.${col}`)}
          </Typography>
        ))}
        {onDeleteCategory && <Box />}
      </Box>

      {/* Data rows */}
      {filteredCategories.map(category => (
        <Box
          key={category.id}
          sx={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderBottom: `1px solid ${colors.border.lighter}`,
            '&:hover': { backgroundColor: colors.background.hover },
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', color: colors.text.muted }}>
            {category.id}
          </Typography>

          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: colors.text.primary }}>
            {category.name || '-'}
          </Typography>

          {FIELD_COLUMNS.map(col => {
            const value = category[col];
            const level = category[`${col}_level`];
            const pendingChange = pendingChanges.find(
              c => c.entityType === 'category' && c.entityId === category.id && c.field === col
            );
            const isPending = !!pendingChange;
            const displayValue = pendingChange ? pendingChange.value : value;
            const isColor = col === 'color';
            return (
              <Box
                key={col}
                onClick={() => onCellClick(category, col)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  px: 0.5,
                  py: 0.25,
                  borderRadius: '4px',
                  ...(isPending ? {
                    backgroundColor: '#FFF7ED',
                    borderLeft: `3px solid ${colors.palette.orange}`,
                  } : {}),
                  '&:hover': {
                    backgroundColor: isPending ? '#FFF7ED' : '#f0f0f0',
                    outline: `1px solid ${colors.border.light}`,
                  },
                }}
              >
                {isColor ? (
                  <Box sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '4px',
                    backgroundColor: displayValue || colors.category.default,
                    border: `1px solid ${colors.border.light}`,
                  }} />
                ) : (
                  <Typography sx={{ fontSize: '0.75rem', color: isPending ? colors.palette.orange : colors.text.primary, fontWeight: isPending ? 600 : 400 }}>
                    {formatValue(displayValue, col, horizonUnits)}
                  </Typography>
                )}
                <LevelBadge level={isPending ? pendingChange.level : level} />
              </Box>
            );
          })}

          {/* Delete button */}
          {onDeleteCategory && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onDeleteCategory(category); }}
                sx={{
                  color: colors.text.muted,
                  '&:hover': { color: colors.status.error },
                  padding: '2px',
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      ))}

      {filteredCategories.length === 0 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ color: colors.text.muted, fontSize: '0.9rem' }}>
            {t('config.noResults')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
